import { useRef, useState, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge } from '@/components/StatusBadge';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { api } from '@/lib/api';
import type { Volume } from '@/types/volume';
import type { Network } from '@/types/network';

interface ResourceDeleteDialogProps {
  resourceType: 'volume' | 'network';
  resource: Volume | Network | null;
  onSubmit: () => Promise<void>;
  open: boolean;
  onClose: () => void;
}

const MAX_LISTED = 5;

/**
 * The Dialog shell is stateless. The body is mounted only while the dialog is open for a resource and
 * keyed by type+id, so checkbox / deleting / error state and the in-use lookup reset on every open
 * without any setState inside an effect.
 */
export function ResourceDeleteDialog({ resourceType, resource, onSubmit, open, onClose }: ResourceDeleteDialogProps) {
  const deletingRef = useRef(false); // written by the body; lets the shell ignore close requests mid-delete
  const { captureOpener, restoreFocus } = useDialogFocusRestore(); // lives in the shell: outlives DeleteBody
  const visible = open && resource !== null;
  return (
    <Dialog
      open={visible}
      onOpenChange={(next) => {
        if (!next && !deletingRef.current) onClose();
      }}
    >
      {visible ? (
        <DeleteBody
          key={`${resourceType}:${resource.id}`}
          resourceType={resourceType}
          resource={resource}
          onSubmit={onSubmit}
          onClose={onClose}
          deletingRef={deletingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface DeleteBodyProps {
  resourceType: 'volume' | 'network';
  resource: Volume | Network;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  deletingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

function DeleteBody({ resourceType, resource, onSubmit, onClose, deletingRef, captureOpener, restoreFocus }: DeleteBodyProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [checked, setChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No interval: one lookup per mount. usePolledData discards stale responses.
  const lookup = usePolledData(() =>
    resourceType === 'volume' ? api.volumes.getContainers(resource.id) : api.networks.getContainers(resource.id),
  );
  const containers = lookup.data ?? [];
  const hasContainers = containers.length > 0;
  // Decision: if the lookup fails we do NOT block deletion. We warn and keep the checkbox path, because the
  // server is the authority and will refuse a delete of a resource that is in use.
  const lookupFailed = lookup.data === undefined && lookup.error !== null;
  const hasDeployment = resource.deployment_id !== undefined && resource.deployment_id !== null;
  const canDelete = !lookup.loading && !hasContainers && checked && !deleting;

  const handleDelete = async () => {
    if (deletingRef.current || !canDelete) return;
    deletingRef.current = true;
    setDeleting(true);
    setError(null);
    try {
      await onSubmit();
      deletingRef.current = false;
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : `Failed to delete ${resourceType}`);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const block = (e: Event) => {
    if (deletingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-md"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle className="text-destructive">Delete {resourceType}</DialogTitle>
        <DialogDescription className="[overflow-wrap:anywhere]">
          You are about to delete {resourceType} &ldquo;{resource.name}&rdquo;. This action cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-muted p-3 text-sm">
          <dt className="font-medium">Instance</dt>
          <dd className="[overflow-wrap:anywhere]">{resource.instance?.name || '-'}</dd>
          <dt className="font-medium">Endpoint</dt>
          <dd className="[overflow-wrap:anywhere]">{resource.endpoint?.name || '-'}</dd>
          <dt className="font-medium">Driver</dt>
          <dd className="[overflow-wrap:anywhere]">{resource.driver}</dd>
        </dl>

        {hasDeployment && (
          <div className="rounded-lg bg-status-warning-soft p-3 text-sm text-status-warning">
            <p className="font-medium">Deployment link warning</p>
            <p className="mt-1">
              This {resourceType} is linked to deployment #{resource.deployment_id}. Deleting may cause the
              deployment to fail.
            </p>
          </div>
        )}

        {lookup.loading ? (
          <div role="status" className="space-y-1">
            <div className="h-4 w-48 animate-pulse rounded bg-muted" aria-hidden="true" />
            <span className="sr-only">Checking which containers use this {resourceType}</span>
          </div>
        ) : null}

        {lookupFailed ? (
          <p className="rounded-lg bg-status-warning-soft p-3 text-sm text-status-warning">
            Couldn&apos;t check which containers use this {resourceType} &mdash; deleting may fail.
          </p>
        ) : null}

        {hasContainers ? (
          <div className="rounded-lg bg-status-danger-soft p-3 text-sm text-status-danger">
            <p className="font-medium">Containers in use ({containers.length})</p>
            <ul className="mt-2 space-y-1">
              {containers.slice(0, MAX_LISTED).map((c) => (
                <li key={c.id} className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  <span className="min-w-0 truncate text-foreground">{c.name}</span>
                </li>
              ))}
            </ul>
            {containers.length > MAX_LISTED ? <p className="mt-1">+{containers.length - MAX_LISTED} more</p> : null}
            <p className="mt-2">Remove these containers first before deleting.</p>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="resource-delete-confirm"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={hasContainers || deleting}
            className="size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <label htmlFor="resource-delete-confirm" className="text-sm">
            I understand the risks and want to delete
          </label>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button ref={cancelRef} type="button" variant="outline" disabled={deleting} onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="destructive" disabled={!canDelete} aria-busy={deleting} onClick={() => void handleDelete()}>
          {deleting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
